<template>
  <div class="flex flex-col h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 overflow-hidden">
    <!-- 상단 네비게이션 -->
    <header class="bg-gradient-to-r from-white via-slate-50 to-white border-b z-40"
      style="border-color: rgba(102, 119, 136, 0.1);">
      <div class="max-w-full mx-auto">
        <div class="h-16 px-4 flex items-center">
          <!-- 왼쪽: 로고 (고정 너비) -->
          <div class="w-60 flex items-center gap-3 select-none cursor-pointer group" @click="$router.push('/')">
            <div class="relative">
              <img src="/logo.png" alt="Bondhub"
                class="h-10 w-auto transition-all duration-300 group-hover:scale-105" />
            </div>
          </div>

          <!-- 중앙: 메인 메뉴 (절대 중앙 배치) -->
          <div class="flex-1 flex justify-center">
            <nav class="flex items-center">
              <div class="flex items-center space-x-8">
                <!-- 크레딧 메뉴 -->
                <div class="relative group">
                  <div class="px-4 py-3 text-sm font-medium cursor-pointer transition-colors duration-200"
                    :class="isActiveCreditMenu ? 'text-[#667788]' : 'text-gray-600 group-hover:text-[#667788]'"
                    @click="$router.push('/')">
                    크레딧
                  </div>
                  <!-- 크레딧 드롭다운 -->
                  <div
                    class="absolute top-full mt-2 w-32 bg-white rounded-xl border shadow-xl py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300"
                    style="border-color: rgba(102, 119, 136, 0.15); box-shadow: 0 10px 25px rgba(102, 119, 136, 0.15); left: 50%; margin-left: -64px;">
                    <router-link to="/"
                      class="block px-4 py-2.5 text-sm text-gray-700 hover:text-[#667788] transition-colors duration-200">
                      호가
                    </router-link>
                    <router-link to="/transaction"
                      class="block px-4 py-2.5 text-sm text-gray-700 hover:text-[#667788] transition-colors duration-200">
                      거래내역
                    </router-link>
                  </div>
                </div>

                <!-- 국고 메뉴 -->
                <div class="relative group">
                  <div class="px-4 py-3 text-sm font-medium cursor-pointer transition-colors duration-200"
                    :class="$route.name === 'NonBenchmarkView' ? 'text-[#667788]' : 'text-gray-600 group-hover:text-[#667788]'"
                    @click="$router.push('/non-benchmark')">
                    국고
                  </div>
                  <!-- 국고 드롭다운 -->
                  <div
                    class="absolute top-full mt-2 w-32 bg-white rounded-xl border shadow-xl py-2 z-50 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300"
                    style="border-color: rgba(102, 119, 136, 0.15); box-shadow: 0 10px 25px rgba(102, 119, 136, 0.15); left: 50%; margin-left: -64px;">
                    <router-link to="/non-benchmark"
                      class="block px-4 py-2.5 text-sm text-gray-700 hover:text-[#667788] transition-colors duration-200">
                      실시간 호가
                    </router-link>
                  </div>
                </div>
              </div>

              <!-- 구분선 -->
              <div class="h-6 w-px bg-gray-300 mx-8"></div>

              <!-- 수급 메뉴 -->
              <div class="relative group">
                <div class="px-4 py-3 text-sm font-medium cursor-pointer transition-colors duration-200"
                  :class="$route.name === 'MarketFlowView' ? 'text-[#667788]' : 'text-gray-600 group-hover:text-[#667788]'"
                  @click="$router.push('/market-flow')">
                  수급
                </div>
              </div>
            </nav>
          </div>

          <!-- 오른쪽: 사용자 정보 + 관리자 메뉴 (고정 너비) -->
          <div class="w-96 flex items-center justify-end gap-4">
            <!-- 관리자 메뉴 -->
            <div v-if="isAdmin" class="relative">
              <button @click="toggleAdminMenu"
                class="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors duration-150">
                <i class="fas fa-cog"></i>
                <span>관리</span>
                <i class="fas fa-chevron-down text-xs" :class="{ 'rotate-180': isAdminMenuOpen }"></i>
              </button>

              <!-- 관리자 드롭다운 메뉴 -->
              <div v-if="isAdminMenuOpen"
                class="absolute right-0 top-full mt-1 w-48 bg-white rounded-lg border shadow-lg py-1 z-50"
                style="border-color: rgba(102, 119, 136, 0.1);">
                <router-link to="/buy" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-shopping-cart mr-2"></i>매수 관리
                </router-link>
                <router-link to="/uncategorized" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-comments mr-2"></i>미분류 채팅
                </router-link>
                <router-link to="/ktb" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-chart-line mr-2"></i>KTB 관리
                </router-link>
                <hr class="my-1" style="border-color: rgba(102, 119, 136, 0.1);">
                <router-link to="/setting/bond" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-certificate mr-2"></i>채권 설정
                </router-link>
                <router-link to="/setting/user" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-users-cog mr-2"></i>사용자 설정
                </router-link>
                <router-link to="/logs" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-history mr-2"></i>사용자 로그
                </router-link>
                <router-link to="/setting/scheduler" class="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                  @click="closeAdminMenu">
                  <i class="fas fa-clock mr-2"></i>스케줄러 설정
                </router-link>
              </div>
            </div>

            <!-- 사용자 정보 -->
            <div class="flex items-center space-x-4">
              <div class="text-sm">
                <span class="font-medium" style="color: #667788;">{{ username }}</span>
                <span class="text-gray-600">님, 안녕하세요</span>
              </div>

              <!-- 텔레그램 문의하기 버튼 (관리자가 아닐 때만 표시) -->
              <button v-if="!isAdmin" @click="openTelegram"
                class="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-800 transition-colors duration-200 rounded-lg hover:bg-gray-50">
                <i class="fab fa-telegram-plane text-base" style="color: #667788;"></i>
                <span>문의하기</span>
              </button>

              <div class="h-6 w-px bg-gray-200"></div>
              <button @click="logout" class="p-2 transition-colors duration-150" style="color: #667788;" title="로그아웃">
                <i class="fas fa-sign-out-alt"></i>
              </button>
              <button v-if="isAdmin" @click="toggleUserList" class="p-2 transition-colors duration-150"
                style="color: #667788;" title="사용자 목록">
                <i class="fas fa-users"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- 메인 콘텐츠 -->
    <main class="flex-1 overflow-hidden bg-gradient-to-br from-slate-50/50 via-white to-slate-100/50">
      <div class="relative h-full">
        <div class="absolute top-0 left-0 right-0 h-8 pointer-events-none"
          style="background: linear-gradient(to bottom, rgba(102, 119, 136, 0.05), transparent);">
        </div>
        <div class="h-full overflow-auto">
          <router-view></router-view>
        </div>
      </div>
    </main>
  </div>

  <!-- 사용자 목록 사이드바 -->
  <div v-if="isUserListOpen"
    class="fixed inset-y-0 right-0 z-40 w-64 shadow-2xl transform transition-transform duration-300 ease-in-out backdrop-blur-xl"
    style="background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(248, 250, 252, 0.9));"
    :class="isUserListOpen ? 'translate-x-0' : 'translate-x-full'">
    <div class="h-full flex flex-col">
      <div class="p-5 flex justify-between items-center border-b"
        style="border-color: rgba(102, 119, 136, 0.1); background: linear-gradient(135deg, rgba(102, 119, 136, 0.05), rgba(90, 107, 122, 0.02));">
        <h2 class="text-lg font-semibold bg-gradient-to-r from-slate-700 to-slate-600 bg-clip-text text-transparent">
          Bondhub 사용자</h2>
        <button @click="toggleUserList"
          class="p-2 rounded-xl transition-all duration-200 hover:shadow-md hover:scale-105"
          style="color: #667788; background: linear-gradient(135deg, rgba(102, 119, 136, 0.1), rgba(90, 107, 122, 0.05));">
          <i class="fas fa-times"></i>
        </button>
      </div>
      <div class="flex-1 overflow-y-auto p-4">
        <ul class="space-y-2">
          <li v-for="user in activeUsers" :key="user.userId"
            class="p-3 rounded-xl transition-all duration-200 border hover:shadow-sm cursor-pointer"
            style="background: linear-gradient(135deg, rgba(255, 255, 255, 0.8), rgba(248, 250, 252, 0.6)); border-color: rgba(102, 119, 136, 0.1);">

            <!-- 첫 번째 줄: 역할 뱃지 + 이름 + 위치 -->
            <div class="flex items-center justify-between mb-1">
              <div class="flex items-center space-x-2">
                <!-- 역할 뱃지 -->
                <span :class="getRoleBadgeClass(user.userType)" class="px-2 py-0.5 text-xs font-medium rounded">
                  {{ getRoleBadgeText(user.userType) }}
                </span>
                <!-- 사용자 이름 -->
                <span class="font-medium text-sm" style="color: #667788;">{{ user.name }}</span>
              </div>
              <!-- 위치 정보 -->
              <span v-if="user.location" class="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {{ user.location }}
              </span>
            </div>

            <!-- 두 번째 줄: Description (truncate 처리) -->
            <div v-if="user.description" class="text-xs text-gray-500 leading-relaxed">
              <span :title="user.description" class="cursor-help" @mouseenter="showFullDescription = user.userId"
                @mouseleave="showFullDescription = null">
                {{ truncateText(user.description, 10) }}
              </span>

              <!-- 호버 시 전체 description 표시 -->
              <div v-if="showFullDescription === user.userId"
                class="absolute z-50 mt-1 p-2 bg-gray-900 text-white text-xs rounded shadow-lg max-w-xs whitespace-normal">
                {{ user.description }}
              </div>
            </div>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch, onUnmounted, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import userService from '@/service/userService';

export default {
  name: 'DashboardLayout',
  setup() {
    const store = useStore();
    const router = useRouter();
    const isUserListOpen = ref(false);
    const isAdminMenuOpen = ref(false);
    const activeUsers = ref([]);
    const showFullDescription = ref(null);
    let refreshInterval = null;

    const username = computed(() => store.getters['auth/username']);
    const isAdmin = computed(() => store.getters['auth/isAdmin']);

    // 크레딧 관련 페이지인지 확인
    const isActiveCreditMenu = computed(() => {
      return ['SellView', 'TransactionView'].includes(router.currentRoute.value.name);
    });

    const toggleAdminMenu = () => {
      isAdminMenuOpen.value = !isAdminMenuOpen.value;
    };

    const closeAdminMenu = () => {
      isAdminMenuOpen.value = false;
    };

    const logout = () => {
      store.dispatch('auth/logout');
      router.push('/login');
    };

    const fetchActiveUsers = async () => {
      try {
        activeUsers.value = await userService.getActiveUserList();
      } catch (error) {
        console.error('사용자 목록 조회 실패:', error);
      }
    };

    const startRefreshInterval = () => {
      fetchActiveUsers();
      refreshInterval = setInterval(fetchActiveUsers, 3000);
    };

    const stopRefreshInterval = () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
        refreshInterval = null;
      }
    };

    const toggleUserList = () => {
      isUserListOpen.value = !isUserListOpen.value;
    };

    const openTelegram = () => {
      window.open('https://t.me/+821057110179', '_blank');
    };

    // 사용자 타입에 따른 뱃지 텍스트 반환
    const getRoleBadgeText = (userType) => {
      const typeMap = {
        MANAGER: 'MNG',
        TRADER: 'TR',
        BROKER: 'BR'
      };
      return typeMap[userType] || userType;
    };

    // 사용자 타입에 따른 뱃지 스타일 클래스 반환
    const getRoleBadgeClass = (userType) => {
      const baseClasses = 'text-white';
      const typeClasses = {
        MANAGER: 'bg-gray-600',
        TRADER: 'bg-gray-700',
        BROKER: 'bg-gray-800'
      };
      return `${baseClasses} ${typeClasses[userType] || 'bg-gray-500'}`;
    };

    // 텍스트 자르기 함수
    const truncateText = (text, maxLength) => {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    };

    // isUserListOpen 상태 변경 감시
    watch(isUserListOpen, newValue => {
      if (newValue) {
        startRefreshInterval();
      } else {
        stopRefreshInterval();
      }
    });

    // 컴포넌트가 제거될 때 인터벌 정리
    onUnmounted(() => {
      stopRefreshInterval();
    });

    // 외부 클릭 시 관리자 메뉴 닫기
    onMounted(() => {
      const handleClickOutside = (event) => {
        if (isAdminMenuOpen.value && !event.target.closest('.relative')) {
          closeAdminMenu();
        }
      };
      document.addEventListener('click', handleClickOutside);

      onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
      });
    });

    return {
      username,
      isAdmin,
      logout,
      isUserListOpen,
      toggleUserList,
      activeUsers,
      isAdminMenuOpen,
      toggleAdminMenu,
      closeAdminMenu,
      isActiveCreditMenu,
      openTelegram,
      showFullDescription,
      getRoleBadgeText,
      getRoleBadgeClass,
      truncateText,
    };
  },
};
</script>

<style scoped>
.rotate-180 {
  transform: rotate(180deg);
}

/* 드롭다운 메뉴 애니메이션 */
.relative>div {
  transition: all 0.3s ease-in-out;
}

/* 메인 메뉴 텍스트 스케일 효과 */
.group>div:first-child {
  transform-origin: center;
}

/* 드롭다운 메뉴 나타나기 애니메이션 */
.group .absolute {
  transform: translateY(-10px) scale(0.95);
}

.group:hover .absolute {
  transform: translateY(0) scale(1);
}

/* 언더라인 애니메이션 */
.group>div:first-child>div {
  transition: width 0.3s ease-out;
}



/* 부드러운 스케일 트랜지션 */
.scale-105 {
  transform: scale(1.05);
}
</style>
